import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Typography, SpacerVertical } from '@klarna/bubble-ui';
import { Message, translator } from 'mage-i18n';
import { MOET_CONSTANTS } from 'moet-core';
import { Row, Column } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import SummaryField from '@/components/SummaryField';

import { CY_APPLICATION_OVERVIEW_PAGE_IDS } from './constants';
import { getPersistedBankAccounts } from '@/containers/BankAccounts/selectors';

const { ACCOUNT_TYPES } = MOET_CONSTANTS;
const tKey = 'products-fe.components.bank_account_summary';
const {
  ACCOUNT_HOLDER,
  ACCOUNT_NUMBER,
  BANK_ACCOUNT_BLOCK,
  BANK_COUNTRY,
  BIC,
  TRANSIT_NUMBER,
  INSTITUTION_NUMBER,
  CURRENCY_CODE,
} = CY_APPLICATION_OVERVIEW_PAGE_IDS;

const SummaryBankAccounts = ({
  noElementsTKey,
  bankAccounts = [],
  showRoutingNumberEU = false,
  showCannotEditDisclaimer = false,
  hideTitle = false,
  t,
  type,
}) =>
  bankAccounts && bankAccounts.length ? (
    bankAccounts.map(({ id, data, type: bankType }, index) => {
      const currencyCode = data.currencyCode || data.currency;
      return (
        <div
          key={`summary-payouts-bankaccount-${id}`}
          {...generateCyElemId(
            `${BANK_ACCOUNT_BLOCK}_${currencyCode}_${
              type === 'defaultBankAccounts' ? 'default' : 'additional'
            }`,
            window,
          )}
        >
          {!hideTitle && (
            <Row>
              <Column>
                <Typography textToken="text-style/text/labels/grande/bold">
                  {t(
                    `${tKey}.subtitle.${type === 'defaultBankAccounts' ? 'default' : 'additional'}`,
                    {
                      index: bankAccounts.length ? index + 1 : '',
                    },
                  )}
                </Typography>
                <SpacerVertical spaceToken="space/100" />
              </Column>
            </Row>
          )}
          {showCannotEditDisclaimer && (
            <Row>
              <Column>
                <Typography textToken="text-style/text/super-paragraphs/body/regular">
                  {t(`${tKey}.no_edit_button`)}
                </Typography>
                <SpacerVertical spaceToken="space/100" />
              </Column>
            </Row>
          )}
          <Row {...generateCyElemId(BANK_ACCOUNT_BLOCK, window)}>
            <Column columns={{ tablet: 6 }}>
              <SummaryField
                content={data.accountHolder}
                titleKey={`${tKey}.account_holder.label`}
                {...generateCyElemId(ACCOUNT_HOLDER, window)}
              />
            </Column>
            {bankType === ACCOUNT_TYPES.AU && (
              <Column columns={{ tablet: 6 }}>
                <SummaryField
                  content={data.bic}
                  titleKey={`${tKey}.bic.label`}
                  {...generateCyElemId(BIC, window)}
                />
              </Column>
            )}
            {ACCOUNT_TYPES.CA === bankType && (
              <>
                <Column columns={{ tablet: 6 }}>
                  <SummaryField
                    content={data.routingNumber?.substr(4, 5)}
                    titleKey={`${tKey}.transit_number.label`}
                    {...generateCyElemId(TRANSIT_NUMBER, window)}
                  />
                </Column>
                <Column columns={{ tablet: 6 }}>
                  <SummaryField
                    content={data.routingNumber?.substr(1, 3)}
                    titleKey={`${tKey}.institution_number.label`}
                    {...generateCyElemId(INSTITUTION_NUMBER, window)}
                  />
                </Column>
              </>
            )}
            {ACCOUNT_TYPES.US === bankType ||
              (ACCOUNT_TYPES.CA !== bankType && showRoutingNumberEU && (
                <Column columns={{ tablet: 6 }}>
                  <SummaryField
                    content={data.routingNumber}
                    titleKey={`${tKey}.${
                      ACCOUNT_TYPES.US === bankType ? 'routing_number' : 'bic'
                    }.label`}
                    {...generateCyElemId(BIC, window)}
                  />
                </Column>
              ))}
            <Column columns={{ tablet: 6 }}>
              <SummaryField
                content={data.accountNumber}
                titleKey={`${tKey}.account_number.label.${
                  ACCOUNT_TYPES.MX === bankType ? bankType : 'default'
                }`}
                {...generateCyElemId(ACCOUNT_NUMBER, window)}
              />
            </Column>
            <Column columns={{ tablet: 6 }}>
              <SummaryField
                content={currencyCode}
                titleKey={`${tKey}.currency_code.label`}
                {...generateCyElemId(CURRENCY_CODE, window)}
              />
            </Column>
            {bankType === ACCOUNT_TYPES.IBAN && (
              <Column columns={{ tablet: 6 }}>
                <SummaryField
                  content={data.country}
                  titleKey={`${tKey}.country_of_bank_account.label`}
                  {...generateCyElemId(BANK_COUNTRY, window)}
                />
              </Column>
            )}
            {index < bankAccounts.length - 1 ? <SpacerVertical spaceToken="space/150" /> : null}
          </Row>
        </div>
      );
    })
  ) : noElementsTKey ? (
    type === 'defaultBankAccounts' ? (
      <Typography
        textToken="text-style/text/super-paragraphs/tall/regular"
        colorToken="colors/text/neutral"
      >
        <Message path={noElementsTKey} />
      </Typography>
    ) : null
  ) : null;

SummaryBankAccounts.propTypes = {
  noElementsTKey: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  bankAccounts: PropTypes.array,
  hideTitle: PropTypes.bool,
  showCannotEditDisclaimer: PropTypes.bool,
  showRoutingNumberEU: PropTypes.bool,
};

export default connect((state, { type }) => ({
  bankAccounts: getPersistedBankAccounts(state).filter(account =>
    // TODO remove country check once we migrated to new bank accounts completely
    type === 'defaultBankAccounts'
      ? account.data.isDefault || account.data.country === 'US'
      : !account.data.isDefault,
  ),
  t: translator(state),
}))(SummaryBankAccounts);
